// src/services/historyRepair.js
import api from './api';
import { signalStore } from './signalStore';
import { selfDecryptKey, bufferToBase64, base64ToBuffer } from './signalCrypto';
import { buildSessionWithContact, encryptPayloadForUser } from './signalSession';
import { SignalProtocolAddress } from '@privacyresearch/libsignal-protocol-typescript';
import { decryptPayloadFromUser } from './signalSession';
import { selfEncryptKey } from './signalCrypto';
import { requestSessionReset } from './signalGroupCrypto';


const _running = new Set();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SESSION_ERRORS = /Bad MAC|No record for device|No session|Invalid prekey id/i;
const KEY_LOST_ERRORS = /Message key not found/i;

export async function healSignalError(socket, senderId, discussionId, err) {
  const message = String(err?.message || '');
  const sessionBroken = SESSION_ERRORS.test(message);
  const keyLost = KEY_LOST_ERRORS.test(message) || err?.code === 'NO_MEMBER_KEY';
  const _repairInFlight = new Set();
  if (!sessionBroken && !keyLost) return;

  // 1) La session est désynchronisée : l'expéditeur supprime la sienne (throttle de 60 s déjà dans la fonction).
  //    On ne supprime PAS la nôtre : un message déjà en route pourrait encore l'utiliser.
  if (sessionBroken && senderId) requestSessionReset(socket, String(senderId), err);

  // 2) Les messages déjà reçus mais illisibles : l'expéditeur nous renvoie leurs clés (throttle de 6 h).
 if (discussionId && !_repairInFlight.has(discussionId)) {
     _repairInFlight.add(discussionId);
     try { await requestHistoryRepair(discussionId); }
     finally { _repairInFlight.delete(discussionId); }
   }

}


async function identityUnchanged(requesterId) {
if (!requesterId || requesterId === 'undefined') {
    console.error('identityUnchanged appelé avec un id invalide', requesterId, new Error().stack);
    throw new Error('requesterId invalide');
  }
  const addr = new SignalProtocolAddress(String(requesterId), 1).toString();
  const known = await signalStore.get(`identity_${addr}`);
  if (!known) return true; // premier contact : TOFU
  const { data } = await api.get(`${API_URL}/api/signal/prekey-bundle/${requesterId}`);
  return bufferToBase64(known) === data.identityKey;
}

export async function fulfilRepair(repair) {
  if (_running.has(repair._id)) return;
  _running.add(repair._id);
  try {
    const B = String(repair.requesterId);

    // Ne jamais partager l'historique avec une identité qui a CHANGÉ (nouvelle identité ≠ recovery)
    if (!(await identityUnchanged(B))) {
      console.warn('Réparation refusée : identité du contact modifiée', B);
      return; // à terme : demander confirmation à l'utilisateur
    }

    // Session neuve avec B (l'ancienne est morte côté B après son recovery)
    await buildSessionWithContact(B);

    let cursor = null, seq = 0, more = true;
    while (more) {
      const { data } = await api.get(`${API_URL}/api/repairs/${repair._id}/sent-keys`, {
        params: { before: cursor || undefined, limit: 200 },
      });
      const entries = [];
      for (const m of data.messages) {
        const push = async (t, src) => {
          try {
            const key = await selfDecryptKey(base64ToBuffer(src.keyForSender), base64ToBuffer(src.keyForSenderIv));
            if (key.byteLength === 32) entries.push({ m: m._id, t, k: bufferToBase64(key) });
          } catch { /* clé illisible : on ignore ce message */ }
        };
        if (m.e2eeData?.keyForSender) await push('text', m.e2eeData);
        for (const [type, f] of Object.entries(m.files || {})) await push(`file:${type}`, f);
      }

      more = data.hasMore && data.messages.length > 0;
      if (data.messages.length) cursor = data.messages[data.messages.length - 1].createdAt;

      if (entries.length || !more) {
        const bytes = new TextEncoder().encode(JSON.stringify(entries));
        const { body, type } = await encryptPayloadForUser(B, bytes);
        await api.post(`${API_URL}/api/repairs/${repair._id}/batch`, {
          seq: seq++, body, type, count: entries.length, last: !more,
        });
      }
    }
  } catch (err) {
    console.error('Erreur fulfilRepair:', err);
  } finally {
    _running.delete(repair._id);
  }
}

export async function fulfilAllPending() {
  try {
    const { data } = await api.get('/api/repairs/pending');
    if (!Array.isArray(data)) {
      console.error('Réponse /repairs/pending inattendue :', typeof data);
      return;
    }
    for (const r of data) {
      if (!r?._id || !r?.requesterId) continue;
      await fulfilRepair(r);
    }
  } catch (err) {
    console.error('Erreur fulfilAllPending:', err);
  }
}

export async function requestHistoryRepair(discussionId) {
  const flag = `repairAsked_${discussionId}`;
  const last = await signalStore.get(flag);
  if (typeof last === 'number' && Date.now() - last < 6 * 3600 * 1000) return;   // 6 h entre deux demandes
  await api.post('/api/repairs', { discussionId });
  await signalStore.put(flag, Date.now());
}

export async function applyReadyBatches(discussionId) {
  const { data: repairs } = await api.get(`${API_URL}/api/repairs/mine`, { params: { discussionId } });

  for (const r of repairs) {
    const seqKey = `repairSeq_${r._id}`;
    let done = (await signalStore.get(seqKey)) ?? -1;
    const batches = [...r.batches].sort((a, b) => a.seq - b.seq).filter(b => b.seq > done);

    for (const b of batches) {
      const bytes = await decryptPayloadFromUser(b.body, b.type, r.targetUserId); // dans l'ordre !
      const entries = JSON.parse(new TextDecoder().decode(bytes));

      const items = [];
      for (const e of entries) {
        const { encrypted, iv } = await selfEncryptKey(base64ToBuffer(e.k));
        items.push({ m: e.m, t: e.t, key: bufferToBase64(encrypted), iv: bufferToBase64(iv) });
      }
      await api.post(`${API_URL}/api/repairs/${r._id}/apply`, {
        items, finished: r.status === 'sent' && b.seq === batches[batches.length - 1].seq,
      });
      await signalStore.put(seqKey, b.seq);   // évite de rejouer un lot (impossible avec Signal)
    }
  }
}
